import React, { useRef, useState } from "react";
import {
  Upload,
  FileText,
  CheckCircle2,
  ExternalLink,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { API } from "../lib/api";
import { toast } from "sonner";

function Stat({ label, value, accent }) {
  return (
    <div className="border border-zinc-800 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
        {label}
      </div>
      <div className={`text-2xl font-mono font-semibold mt-1 ${accent || "text-zinc-50"}`}>
        {value}
      </div>
    </div>
  );
}

export function ImportDccDialog({ onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Pick a CSV file first");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`${API}/import/dcc`, {
        method: "POST",
        body: fd,
      });
      if (!resp.ok) throw new Error(`Server ${resp.status}`);
      const data = await resp.json();
      setResult(data);
      toast.success(
        `Imported · ${data.imported_new} new, ${data.merged_existing} merged`
      );
      onImported?.(data);
    } catch (e) {
      toast.error("Import failed", { description: e?.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <Button
        data-testid="import-dcc-btn"
        onClick={() => setOpen(true)}
        variant="outline"
        className="border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-100 rounded-md"
      >
        <Upload className="w-4 h-4 mr-2" />
        Import from DCC
      </Button>
      <DialogContent
        data-testid="import-dcc-modal"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl"
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-50 flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-400" />
            Import California DCC License CSV
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
            Imports <strong className="text-zinc-300">storefront retailers only</strong>{" "}
            (Type-10, active). Delivery-only, cultivators, manufacturers,
            distributors and labs are excluded automatically. Duplicates are
            merged by name + city — your manual edits are never overwritten.
          </p>
        </DialogHeader>

        {!result ? (
          <div className="mt-2 space-y-4">
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-4 py-3 text-xs text-zinc-400 leading-relaxed">
              <div className="font-medium text-zinc-200 mb-1.5 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                How to get the CSV
              </div>
              <ol className="list-decimal list-inside space-y-0.5 ml-1">
                <li>
                  Go to{" "}
                  <a
                    href="https://search.cannabis.ca.gov/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-400 hover:underline inline-flex items-center gap-0.5"
                  >
                    search.cannabis.ca.gov
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  Filter to <code className="text-zinc-300">License Type → Cannabis - Retailer</code>{" "}
                  and <code className="text-zinc-300">Status → Active</code>
                </li>
                <li>Click <strong>Download</strong> → CSV, then upload it below</li>
              </ol>
            </div>

            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid="import-dcc-dropzone"
              className="w-full border-2 border-dashed border-zinc-800 hover:border-emerald-500/40 hover:bg-zinc-900/40 rounded-lg p-6 transition-colors duration-150 cursor-pointer text-center"
            >
              <Upload className="w-6 h-6 mx-auto text-zinc-600 mb-2" />
              <div className="text-sm text-zinc-200 font-medium">
                {file ? file.name : "Click to choose CSV file"}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB · ready to upload`
                  : "Or drag & drop — accepts .csv"}
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="import-dcc-file"
              />
            </button>
          </div>
        ) : (
          <div className="mt-2 space-y-4" data-testid="import-dcc-result">
            <div className="grid grid-cols-2 gap-3">
              <Stat
                label="Rows in file"
                value={result.total_rows_in_file}
              />
              <Stat
                label="Storefront retailers"
                value={result.retail_storefront_rows}
                accent="text-emerald-300"
              />
              <Stat
                label="New dispensaries"
                value={result.imported_new}
                accent="text-emerald-400"
              />
              <Stat
                label="Merged"
                value={result.merged_existing}
                accent="text-amber-300"
              />
            </div>

            <div className="text-[11px] font-mono text-zinc-500 leading-relaxed">
              Skipped: {result.skipped_non_retail} non-retail ·{" "}
              {result.skipped_inactive} inactive ·{" "}
              {result.skipped_no_name} missing name
            </div>

            {result.examples?.length > 0 && (
              <div className="border border-zinc-800 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mb-1.5 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  Examples
                </div>
                <ul className="text-xs text-zinc-300 space-y-0.5">
                  {result.examples.map((e) => (
                    <li key={e} className="font-mono">{e}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2">
          {!result ? (
            <>
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
              >
                Cancel
              </Button>
              <Button
                data-testid="import-dcc-submit"
                onClick={handleSubmit}
                disabled={!file || uploading}
                className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing…
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Run import
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={reset}
                className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
              >
                Import another file
              </Button>
              <Button
                data-testid="import-dcc-done"
                onClick={() => setOpen(false)}
                className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
              >
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ImportDccDialog;
